import { css } from '@emotion/react'
import { FormLabel, HFlow, SelectHelperMenuItem, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { CustomSelectFieldProps, resolveName, SelectField } from 'components/form/core'
import { HLabel } from 'components/HLabel'
import { InfoIcon } from 'components/InfoIcon'
import { Cid11SelectFieldDocument } from 'graphql/hooks.generated'
import { Cid11SelectFieldQuery, Cid11SelectFieldQueryVariables } from 'graphql/types.generated'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-final-form'

import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'
import { Cid11SelectFieldModel } from './model-cid11'

export type Cid11SelectFieldProps = CustomSelectFieldProps<Cid11SelectFieldModel> & {
  cid10Id?: string
  isCid10Required?: boolean
  capitulos?: string[]
  hintMessage?: string
  loadItemsOnOpen?: boolean
}

interface EmptyItemParams {
  isCid10Required: boolean
  hasCid10: boolean
  hasQuery: boolean
  isAwaitingQuery: boolean
}

const DISABLED_TOOLTIP = 'É necessário informar CID-10 para habilitar o campo de CID-11'
const HEADER_TEXT = 'CID-11 relacionadas com a CID-10 selecionada'
const DEFAULT_HINT_MESSAGE =
  'A CID-11 é a 11ª revisão da Classificação Internacional de Doenças, um novo padrão global da OMS para codificar e registrar doenças e condições de saúde.'
const EMPTY_ITEM_TEXT = 'Nenhum resultado encontrado.'
const NO_RELATION_AVAILABLE =
  'O CID-10 informado não possui um CID-11 correspondente. Devido à diferenças entre as versões da classificação, alguns códigos não apresentam equivalência direta.'

const MIN_QUERY_LENGTH = 2
const AWAITING_QUERY_TEXT = 'Digite para buscar um CID-11.'

const getEmptyItemText = ({ isCid10Required, hasCid10, hasQuery, isAwaitingQuery }: EmptyItemParams) => {
  if (isAwaitingQuery) return AWAITING_QUERY_TEXT
  if (!isCid10Required || hasQuery) return EMPTY_ITEM_TEXT
  return hasCid10 ? NO_RELATION_AVAILABLE : ''
}

const createEmptyItem = (params: EmptyItemParams) => () => (
  <SelectHelperMenuItem>{getEmptyItemText(params)}</SelectHelperMenuItem>
)

const extractItems = (data: Cid11SelectFieldQuery | null): Cid11SelectFieldModel[] =>
  data?.cids11?.content?.filter((item): item is Cid11SelectFieldModel => item != null) ?? []

const itemToString = (item: Cid11SelectFieldModel | null): string | null =>
  item ? `${item.nome.toUpperCase()} - ${item.codigo}` : null

const renderItem = (item: Cid11SelectFieldModel) => (
  <VFlow vSpacing={0}>
    <Text fontWeight='bold'>{item.nome.toUpperCase()}</Text>
    <HLabel title='Código '>{item.codigo}</HLabel>
  </VFlow>
)

function PrependHeader() {
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])

  return (
    <div css={styles.headerItem}>
      <Text fontWeight='bold' style={styles.headerItemText}>
        {HEADER_TEXT}
      </Text>
    </div>
  )
}

export function Cid11SelectField(props: Readonly<Cid11SelectFieldProps>) {
  const {
    cid10Id,
    label,
    isCid10Required = true,
    capitulos,
    hintMessage = DEFAULT_HINT_MESSAGE,
    loadItemsOnOpen = true,
    disabled: disabledProp,
    onFilterChange: onFilterChangeProp,
    ...rest
  } = props

  const form = useForm()
  const [inputQuery, setInputQuery] = useState('')
  const hasCid10 = !!cid10Id
  const isCid10RequiredAndMissing = isCid10Required && !hasCid10
  const disabled = disabledProp || isCid10RequiredAndMissing
  const resolvedName = resolveName(props.name)
  const prevCid10Id = useRef(cid10Id)

  useEffect(() => {
    const hasCid10Changed = prevCid10Id.current !== cid10Id
    const hadPreviousCid10 = prevCid10Id.current !== undefined

    if (isCid10Required && hadPreviousCid10 && hasCid10Changed) {
      form.change(resolvedName, undefined)
      setInputQuery('')
    }

    prevCid10Id.current = cid10Id
  }, [cid10Id, isCid10Required, form, resolvedName])

  const skip = useCallback(
    (inputString: string) =>
      isCid10RequiredAndMissing || (!loadItemsOnOpen && inputString.trim().length < MIN_QUERY_LENGTH),
    [isCid10RequiredAndMissing, loadItemsOnOpen]
  )

  const variables = useCallback(
    (inputQuery: string): Cid11SelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        capitulos,
        cid10Id,
        pageParams: {
          ...DEFAULT_SELECT_PAGE_PARAM,
          sort: ['nome'],
        },
      },
    }),
    [cid10Id, capitulos]
  )

  const {
    skipping,
    selectProps: { onFilterChange: onFilterChangeBase, ...selectProps },
  } = useAsyncQuerySelect<Cid11SelectFieldModel, Cid11SelectFieldQuery, Cid11SelectFieldQueryVariables>({
    query: Cid11SelectFieldDocument,
    extractItems,
    variables,
    skip,
    debounceTime: 500,
  })

  const onFilterChange = useCallback<NonNullable<typeof onFilterChangeBase>>(
    (inputString, downshift) => {
      setInputQuery(inputString ?? '')
      onFilterChangeBase?.(inputString, downshift)
      onFilterChangeProp?.(inputString, downshift)
    },
    [onFilterChangeBase, onFilterChangeProp]
  )

  const hasQuery = !!inputQuery.trim()
  const isAwaitingQuery = !loadItemsOnOpen && !!skipping
  const components = useMemo(
    () => ({
      EmptyItem: createEmptyItem({ isCid10Required, hasCid10, hasQuery, isAwaitingQuery }),
      ...(isCid10Required && { PrependItem: PrependHeader }),
    }),
    [isCid10Required, hasCid10, hasQuery, isAwaitingQuery]
  )

  return (
    <VFlow vSpacing={0.5}>
      {label && (
        <HFlow alignItems='center' hSpacing={0.25}>
          <FormLabel label={label} />
          <InfoIcon icon='infoCircleFilled' text={hintMessage} />
        </HFlow>
      )}
      <Tooltip text={isCid10RequiredAndMissing ? DISABLED_TOOLTIP : undefined}>
        <SelectField<Cid11SelectFieldModel>
          itemToString={itemToString}
          renderItem={renderItem}
          components={components}
          disabled={disabled}
          onFilterChange={onFilterChange}
          {...selectProps}
          {...rest}
        />
      </Tooltip>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  headerItem: css`
    padding: 0.5rem 1rem;
    background-color: ${theme.pallete.primary.c90};
  `,
  headerItemText: css`
    color: ${theme.pallete.primary.c40};
  `,
})
